import { Head, useForm, usePage } from "@inertiajs/react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Header } from "@/components/header"
import { displayName } from "@/lib/utils"
import type { SharedProps } from "@/types"
import { Shuffle } from "lucide-react"

function getDiceBearUrl(seed: string) {
  return `https://api.dicebear.com/9.x/bottts-neutral/svg?seed=${seed}`
}

function randomSeed() {
  return Math.random().toString(36).substring(2)
}

export default function Edit() {
  const { currentUser } = usePage<SharedProps>().props

  const { data, setData, patch, processing, transform } = useForm({
    display_name: currentUser?.display_name || "",
    custom_avatar_url: currentUser?.custom_avatar_url || "",
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    transform((data) => ({ user: data }))
    patch(`/users/${currentUser?.id}`)
  }

  const defaultAvatarUrl = getDiceBearUrl(`user-${currentUser?.id}`)
  const previewAvatarUrl = data.custom_avatar_url || defaultAvatarUrl

  return (
    <div className="min-h-screen">
      <Head title="Settings" />
      <Header />

      <main className="container mx-auto px-4 py-8 max-w-lg">
        <h1 className="text-2xl font-mono font-bold mb-6">SETTINGS</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Profile</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="display_name">Display name</Label>
                <Input
                  id="display_name"
                  value={data.display_name}
                  onChange={(e) => setData("display_name", e.target.value)}
                  maxLength={24}
                  placeholder="Anonymous"
                />
                <p className="text-xs text-muted-foreground">
                  {data.display_name.length}/24 characters
                </p>
              </div>

              <div className="space-y-3">
                <Label>Avatar</Label>
                <div className="flex gap-4 items-center">
                  <Avatar className="h-16 w-16 shrink-0">
                    <AvatarImage src={previewAvatarUrl ?? undefined} />
                    <AvatarFallback>
                      {currentUser ? displayName(currentUser)[0] : "?"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="space-y-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setData("custom_avatar_url", getDiceBearUrl(randomSeed()))}
                    >
                      <Shuffle className="h-4 w-4 mr-2" />
                      Randomize
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      Generate a random robot avatar
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="custom_avatar_url" className="text-xs text-muted-foreground">
                    Or use a custom URL
                  </Label>
                  <Input
                    id="custom_avatar_url"
                    value={data.custom_avatar_url}
                    onChange={(e) => setData("custom_avatar_url", e.target.value)}
                    placeholder="https://..."
                  />
                </div>

                {data.custom_avatar_url && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setData("custom_avatar_url", "")}
                    className="text-xs"
                  >
                    Reset to default
                  </Button>
                )}
              </div>

              <Button type="submit" disabled={processing}>
                {processing ? "Saving..." : "Save"}
              </Button>
            </CardContent>
          </Card>
        </form>
      </main>
    </div>
  )
}
